import * as React from 'react';

import NoImage from '../../assets/images/no-image.png';
import useImageLoader from '../../hooks/useImageLoader';
import { ChatMessageTypes, ChatMessageTypesClassMap } from '../../store';
import useDrawerStore from '../../store/drawer';
import ProductSummary, { ProductSummaryModel } from '../ProductSummary';
import ProductSpecFeature from '../product-specification/Feature';
import ProductSpecFeatureGroup from '../product-specification/FeatureGroup';
import { Feature, FeatureGroup } from '../product-specification/types';

export interface ProductCardMessageContent {
	productSummary: ProductSummaryModel;
	featureGroups: FeatureGroup[];
}

export interface ProductCardMessageProps {
	messageId: string;
	productData: ProductCardMessageContent;
}

export const dataTestId = `${
	ChatMessageTypesClassMap[ChatMessageTypes.ProductCard]
}-message`;

export const ProductCardMessage: React.FC<ProductCardMessageProps> = ({
	messageId,
	productData,
}) => {
	const { productSummary, featureGroups } = productData;
	const spotlightFeatures = featureGroups
		.reduce(
			(acc, featureGroup) => [
				...acc,
				...featureGroup.features.filter(
					(feature) => feature.isSpotlightFeature
				),
			],
			[] as Feature[]
		)
		.sort(
			(featureA, featureB) =>
				featureB.confidenceScore - featureA.confidenceScore
		);
	const [productImage] = useImageLoader([
		{ src: productSummary.imageUrl, placeholder: NoImage },
	]);
	const drawerStore = useDrawerStore();

	function handleSpecCta() {
		drawerStore.setOnClose(() => {
			// TODO: Add analytics
			drawerStore.setIsOpen(false);
		});
		drawerStore.setOptions({
			'aria-labelledby': 'chatbot-drawer-article-title',
			'data-tag-area': 'product card spec slideout',
			elementToReturnFocusOnClose: document.activeElement
				? (document.activeElement as HTMLElement)
				: undefined,
			// TODO: Remove type casting. It doesn't like the data attributes
		} as React.HTMLProps<HTMLDivElement>);
		drawerStore.setContent(() => (
			<React.Fragment>
				<h3 id="chatbot-drawer-article-title" className="text-display-sm">
					Product Specification
				</h3>

				{featureGroups.map((featureGroup) => (
					<ProductSpecFeatureGroup
						key={featureGroup.featureGroupName}
						uniqueId={messageId}
						featureGroup={featureGroup}
					/>
				))}
			</React.Fragment>
		));
		drawerStore.setIsOpen(true);
	}

	return (
		<div
			data-testid={dataTestId}
			data-tag-area="product card message"
			className="mx-2"
		>
			<div className="border-gray-40 text-caption font-regular mb-2 break-words bg-white border border-solid rounded-md">
				<ProductSummary
					taggingPrefix="product-card"
					product={{ ...productSummary, imageUrl: productImage.src }}
				/>
				{spotlightFeatures.length ? (
					<table
						data-testid="spotlight-product-features"
						className="relative z-20 w-full"
					>
						<tbody>
							{spotlightFeatures.map((feature, featureIdx) => (
								<ProductSpecFeature
									key={`spotlight-feature-${feature.featureName}`}
									className={featureIdx % 2 === 0 ? 'bg-gray-10' : ''}
									tooltipId={`spotlight-feature-${feature.featureName}-${messageId}`}
									feature={feature}
								/>
							))}
						</tbody>
					</table>
				) : (
					<hr className="border-gray-40 h-0 m-0 border-0 border-b border-solid" />
				)}
				<button
					data-tag-name="product card spec"
					data-tag-sku={productSummary.productCode}
					data-tag-spotlight-features={JSON.stringify(
						spotlightFeatures.map((feature) => feature.featureName)
					)}
					type="button"
					className="c-chat-product-card-spec-cta hover:bg-gray-10 text-cta-action text-title-sm flex items-center justify-between w-full px-3 bg-white rounded-md rounded-t-none cursor-pointer"
					style={{ paddingTop: '15px', paddingBottom: '15px' }}
					onClick={handleSpecCta}
				>
					Product specification
					<span
						className="ico ico-chevron-right text-cta-action inline-flex text-lg leading-none"
						style={{ fontSize: '24px' }}
					/>
				</button>
			</div>
		</div>
	);
};

export default ProductCardMessage;
